import { useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import {
    ArrowLeft,
    ArrowUpRight,
    CheckCircle2,
    Download,
    FileText,
} from "lucide-react";
import {
    PROJETO,
    TIMELINE,
    CRONOGRAMA,
    PRESTACAO,
    IMPACTO,
    IMAGES,
    SITE,
    PARTNERS,
} from "@/data/site";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";

const EASE = [0.76, 0, 0.24, 1];

const MaskLine = ({ children, delay = 0, className = "" }) => (
    <span className={`mask-line ${className}`}>
        <motion.span
            className="block"
            initial={{ y: "115%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 1.1, ease: EASE, delay }}
        >
            {children}
        </motion.span>
    </span>
);

const Reveal = ({ children, delay = 0 }) => (
    <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: EASE, delay }}
    >
        {children}
    </motion.div>
);

// ============================================================
// HERO
// ============================================================
const ProjetoHero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

    return (
        <section
            ref={ref}
            data-testid="projeto-hero"
            style={{ position: "relative" }}
            className="min-h-[100svh] w-full overflow-hidden bg-[var(--ink)]"
        >
            <motion.div
                style={{ y, scale }}
                className="absolute inset-0 -z-10"
            >
                <img
                    src={IMAGES.community}
                    alt="Meninas Vencedoras — projeto Eletrobras"
                    className="hero-media h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-[var(--ink)]/78" />
                <div className="absolute inset-0 bg-gradient-to-b from-[var(--ink)]/50 via-transparent to-[var(--ink)]" />
            </motion.div>

            <div className="container-x pt-28 md:pt-32">
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.8, ease: EASE }}
                    className="flex items-center justify-between text-white/60 font-body text-[11px] tracking-[0.32em] uppercase"
                >
                    <Link
                        to="/"
                        data-testid="projeto-back-home"
                        className="link-sweep flex items-center gap-2 text-[var(--sun)]"
                    >
                        <ArrowLeft size={14} strokeWidth={2.5} />
                        Voltar ao site
                    </Link>
                    <span className="hidden sm:inline">
                        Processo {PROJETO.processo} · SLI {PROJETO.sli}
                    </span>
                    <span>N°02 / 2025</span>
                </motion.div>
            </div>

            <div className="container-x relative z-10 pt-8 md:pt-12">
                <div className="text-white/50 font-body text-[11px] tracking-[0.32em] uppercase mb-4">
                    Projeto · {PROJETO.periodo}
                </div>
                <h1
                    data-testid="projeto-headline"
                    className="font-display text-white text-[13vw] md:text-[10vw] lg:text-[8.5vw]"
                >
                    <MaskLine delay={0.05}>Meninas</MaskLine>
                    <MaskLine delay={0.18} className="text-[var(--sun)] italic">
                        <span className="font-editorial italic">vencedoras.</span>
                    </MaskLine>
                    <MaskLine delay={0.32}>No campo,</MaskLine>
                    <MaskLine delay={0.44}>na comunidade.</MaskLine>
                </h1>
            </div>

            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.15, duration: 0.9, ease: EASE }}
                className="container-x mt-14 md:mt-20 grid grid-cols-12 gap-6 pb-24"
            >
                <div className="col-span-12 md:col-span-6">
                    <p className="font-body text-white/75 text-[15px] md:text-[16px] leading-relaxed max-w-lg">
                        {PROJETO.manifesto}
                    </p>
                </div>
                <div className="col-span-12 md:col-span-4 md:col-start-9 space-y-3 text-white/70 font-body text-[13px]">
                    <div className="flex items-center gap-3">
                        <span className="inline-block h-px w-8 bg-white/40" />
                        <span className="tracking-[0.24em] uppercase text-[11px]">
                            Patrocínio
                        </span>
                    </div>
                    <p className="font-editorial italic text-2xl text-white">
                        {PROJETO.patrocinador}
                    </p>
                    <p className="text-[12px] text-white/60">
                        {PROJETO.apoio}
                    </p>
                </div>
            </motion.div>
        </section>
    );
};

// ============================================================
// OBJETIVOS + PÚBLICO
// ============================================================
const ProjetoObjetivos = () => (
    <section
        data-testid="projeto-objetivos"
        className="relative bg-[var(--paper)] text-[var(--ink)] py-24 md:py-32"
    >
        <div className="container-x">
            <div className="grid grid-cols-12 gap-8">
                <div className="col-span-12 md:col-span-5">
                    <div className="flex items-center gap-3 text-[11px] tracking-[0.32em] uppercase text-[var(--ink)]/60 font-body">
                        <span className="inline-block h-px w-10 bg-[var(--ink)]/40" />
                        <span>Objetivos</span>
                    </div>
                    <Reveal>
                        <h2 className="font-display text-[13vw] md:text-[6.5vw] lg:text-[5.5vw] mt-6">
                            O que <br />
                            <span className="font-editorial italic font-medium text-[var(--ink-2)]">
                                buscamos.
                            </span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <div className="mt-8 border-2 border-[var(--ink)] p-6 max-w-md">
                            <div className="text-[10px] tracking-[0.32em] uppercase font-body text-[var(--ink)]/60">
                                Público-alvo
                            </div>
                            <p className="mt-3 font-editorial italic text-xl leading-tight">
                                {PROJETO.publicoAlvo}
                            </p>
                            <div className="mt-6 text-[10px] tracking-[0.32em] uppercase font-body text-[var(--ink)]/60">
                                Modalidades
                            </div>
                            <div className="mt-3 flex flex-wrap gap-2">
                                {PROJETO.modalidades.map((m) => (
                                    <span
                                        key={m}
                                        data-testid={`modalidade-${m}`}
                                        className="inline-block bg-[var(--ink)] text-[var(--sun)] px-3 py-1.5 text-[11px] tracking-[0.14em] font-body uppercase"
                                    >
                                        {m}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </Reveal>
                </div>

                <div className="col-span-12 md:col-span-6 md:col-start-7">
                    <ol className="mt-14 md:mt-24 space-y-0">
                        {PROJETO.objetivosGerais.map((obj, i) => (
                            <Reveal key={i} delay={i * 0.05}>
                                <li
                                    data-testid={`objetivo-${i}`}
                                    className="grid grid-cols-12 gap-4 py-6 border-b border-[var(--ink)]/15"
                                >
                                    <span className="col-span-2 chapter-num text-3xl text-[var(--ink)]/70">
                                        0{i + 1}
                                    </span>
                                    <p className="col-span-10 font-editorial italic text-2xl leading-tight text-[var(--ink)]/85">
                                        {obj}
                                    </p>
                                </li>
                            </Reveal>
                        ))}
                    </ol>
                </div>
            </div>
        </div>
    </section>
);

// ============================================================
// IMPACTO — Métricas
// ============================================================
const ProjetoImpacto = () => (
    <section
        data-testid="projeto-impacto"
        className="relative bg-[var(--ink)] text-white py-20 md:py-24 border-y border-white/10"
    >
        <div className="container-x">
            <div className="flex items-center gap-3 text-[11px] tracking-[0.32em] uppercase text-white/50 font-body mb-10">
                <span className="inline-block h-px w-10 bg-white/40" />
                <span>Impacto</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-y-14 gap-x-6 border-t border-white/10 pt-14">
                {IMPACTO.map((s, i) => (
                    <motion.div
                        key={s.label}
                        data-testid={`impacto-${i}`}
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{
                            duration: 0.9,
                            ease: EASE,
                            delay: i * 0.08,
                        }}
                        className={`px-2 md:px-6 ${
                            i > 0 ? "md:border-l md:border-white/10" : ""
                        }`}
                    >
                        <div className="font-display text-[13vw] md:text-[6vw] lg:text-[4.8vw] leading-none">
                            <span
                                className={
                                    i % 2 === 0
                                        ? "text-white"
                                        : "stroke-text text-[var(--sun)]"
                                }
                            >
                                {s.valor}
                            </span>
                        </div>
                        <div className="mt-4 text-[11px] tracking-[0.32em] uppercase text-white/60 font-body">
                            {s.label}
                        </div>
                    </motion.div>
                ))}
            </div>
        </div>
    </section>
);

// ============================================================
// TIMELINE
// ============================================================
const ProjetoTimeline = () => (
    <section
        id="timeline"
        data-testid="projeto-timeline"
        className="relative bg-[var(--paper)] text-[var(--ink)] py-24 md:py-32"
    >
        <div className="container-x">
            <div className="grid grid-cols-12 gap-6 mb-16">
                <div className="col-span-12 md:col-span-5">
                    <div className="flex items-center gap-3 text-[11px] tracking-[0.32em] uppercase text-[var(--ink)]/60 font-body">
                        <span className="inline-block h-px w-10 bg-[var(--ink)]/40" />
                        <span>Timeline</span>
                    </div>
                    <Reveal>
                        <h2 className="font-display text-[13vw] md:text-[6.5vw] lg:text-[5.5vw] mt-6">
                            De onde <br />
                            <span className="font-editorial italic font-medium text-[var(--ink-2)]">
                                viemos.
                            </span>
                        </h2>
                    </Reveal>
                </div>
                <div className="col-span-12 md:col-span-5 md:col-start-8 flex items-end">
                    <p className="font-body text-[15px] leading-relaxed text-[var(--ink)]/75 max-w-md">
                        Vinte anos de história, marcos que atravessam gerações
                        e uma linha do tempo que agora inclui o maior projeto
                        institucional que já executamos.
                    </p>
                </div>
            </div>

            <div className="relative">
                {/* Central vertical line */}
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-[var(--ink)]/20" />

                <ol className="space-y-16 md:space-y-24">
                    {TIMELINE.map((t, i) => (
                        <Reveal key={i} delay={i * 0.04}>
                            <li
                                data-testid={`timeline-item-${i}`}
                                className={`relative grid grid-cols-12 gap-6 ${
                                    i % 2 === 0 ? "md:text-right" : ""
                                }`}
                            >
                                {/* Dot */}
                                <span className="absolute left-4 md:left-1/2 -translate-x-1/2 top-2 h-3 w-3 bg-[var(--sun)] border-[1.5px] border-[var(--ink)] rounded-full z-10" />

                                <div
                                    className={`col-span-12 md:col-span-5 pl-12 md:pl-0 ${
                                        i % 2 === 0
                                            ? "md:col-start-1 md:pr-16"
                                            : "md:col-start-8 md:pl-16"
                                    }`}
                                >
                                    <div className="font-display text-[11px] tracking-[0.32em] uppercase text-[var(--ink)]/60">
                                        {t.data}
                                    </div>
                                    <h3 className="mt-3 font-editorial italic text-3xl md:text-4xl text-[var(--ink-2)] leading-tight">
                                        {t.titulo}
                                    </h3>
                                    <p className="mt-4 font-body text-[15px] leading-relaxed text-[var(--ink)]/75">
                                        {t.desc}
                                    </p>
                                </div>
                            </li>
                        </Reveal>
                    ))}
                </ol>
            </div>
        </div>
    </section>
);

// ============================================================
// CRONOGRAMA
// ============================================================
const ProjetoCronograma = () => (
    <section
        id="cronograma"
        data-testid="projeto-cronograma"
        className="relative bg-[var(--ink)] text-white py-24 md:py-32"
    >
        <div className="container-x">
            <div className="grid grid-cols-12 gap-6 mb-16">
                <div className="col-span-12 md:col-span-6">
                    <div className="flex items-center gap-3 text-[11px] tracking-[0.32em] uppercase text-white/50 font-body">
                        <span className="inline-block h-px w-10 bg-white/40" />
                        <span>Cronograma físico</span>
                    </div>
                    <Reveal>
                        <h2 className="font-display text-[13vw] md:text-[6.5vw] lg:text-[5.5vw] mt-6">
                            12 meses <br />
                            <span className="font-editorial italic font-medium text-[var(--sun)]">
                                em campo.
                            </span>
                        </h2>
                    </Reveal>
                </div>
                <div className="col-span-12 md:col-span-4 md:col-start-9 flex items-end">
                    <p className="font-body text-[15px] leading-relaxed text-white/70">
                        Cada mês é uma etapa concreta — da mobilização inicial
                        ao relatório final entregue ao patrocinador.
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                {CRONOGRAMA.map((c, i) => (
                    <Reveal key={c.mes} delay={(i % 3) * 0.05}>
                        <article
                            data-testid={`cronograma-item-${i}`}
                            className="h-full border border-white/10 p-6 md:p-7 bg-white/[0.02] hover:border-[var(--sun)]/70 transition-colors duration-300"
                        >
                            <div className="flex items-center justify-between">
                                <span className="font-display text-[11px] tracking-[0.32em] text-[var(--sun)]">
                                    {c.mes}
                                </span>
                                <span className="chapter-num text-2xl text-white/40">
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                            </div>
                            <h3 className="mt-5 font-editorial italic text-2xl md:text-3xl text-white leading-tight">
                                {c.etapa}
                            </h3>
                            <ul className="mt-5 space-y-2 font-body text-[13px] text-white/70">
                                {c.atividades.map((a) => (
                                    <li
                                        key={a}
                                        className="flex items-start gap-2"
                                    >
                                        <CheckCircle2
                                            size={14}
                                            className="mt-1 text-[var(--sun)] flex-shrink-0"
                                        />
                                        <span>{a}</span>
                                    </li>
                                ))}
                            </ul>
                        </article>
                    </Reveal>
                ))}
            </div>
        </div>
    </section>
);

// ============================================================
// PRESTAÇÃO DE CONTAS
// ============================================================
const ProjetoPrestacao = () => (
    <section
        id="prestacao"
        data-testid="projeto-prestacao"
        className="relative bg-[var(--paper)] text-[var(--ink)] py-24 md:py-32"
    >
        <div className="container-x">
            <div className="grid grid-cols-12 gap-6 mb-16">
                <div className="col-span-12 md:col-span-6">
                    <div className="flex items-center gap-3 text-[11px] tracking-[0.32em] uppercase text-[var(--ink)]/60 font-body">
                        <span className="inline-block h-px w-10 bg-[var(--ink)]/40" />
                        <span>Prestação de contas · Transparência</span>
                    </div>
                    <Reveal>
                        <h2 className="font-display text-[13vw] md:text-[6.5vw] lg:text-[5.5vw] mt-6">
                            Cada real, <br />
                            <span className="font-editorial italic font-medium text-[var(--ink-2)]">
                                à vista.
                            </span>
                        </h2>
                    </Reveal>
                </div>
                <div className="col-span-12 md:col-span-4 md:col-start-9 flex items-end">
                    <p className="font-body text-[15px] leading-relaxed text-[var(--ink)]/75 max-w-md">
                        Compromisso institucional com a Eletrobras, o
                        Ministério do Esporte e — sobretudo — com a comunidade
                        que nos acompanha.
                    </p>
                </div>
            </div>

            {/* Totais */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-14">
                {[
                    {
                        label: "Aprovado",
                        val: PRESTACAO.valorAprovado,
                        testid: "total-aprovado",
                    },
                    {
                        label: "Captado",
                        val: PRESTACAO.valorCaptado,
                        testid: "total-captado",
                    },
                    {
                        label: "Executado",
                        val: PRESTACAO.valorExecutado,
                        testid: "total-executado",
                    },
                ].map((item, i) => (
                    <Reveal key={item.label} delay={i * 0.06}>
                        <div
                            data-testid={item.testid}
                            className="border-2 border-[var(--ink)] p-6 md:p-8"
                        >
                            <div className="text-[10px] tracking-[0.32em] uppercase font-body text-[var(--ink)]/60">
                                Valor {item.label}
                            </div>
                            <div className="mt-3 font-display text-4xl md:text-5xl text-[var(--ink)]">
                                {item.val}
                            </div>
                            {item.label === "Executado" && (
                                <div className="mt-4">
                                    <div className="h-2 w-full bg-[var(--ink)]/10 relative overflow-hidden">
                                        <motion.div
                                            initial={{ width: "0%" }}
                                            whileInView={{
                                                width: `${PRESTACAO.percentualExecutado}%`,
                                            }}
                                            viewport={{
                                                once: true,
                                                margin: "-40px",
                                            }}
                                            transition={{
                                                duration: 1.4,
                                                ease: EASE,
                                                delay: 0.2,
                                            }}
                                            className="h-full bg-[var(--sun)] border-r-2 border-[var(--ink)]"
                                        />
                                    </div>
                                    <div className="mt-2 font-body text-[11px] tracking-[0.24em] uppercase text-[var(--ink)]/60">
                                        {PRESTACAO.percentualExecutado}%
                                        executado
                                    </div>
                                </div>
                            )}
                        </div>
                    </Reveal>
                ))}
            </div>

            {/* Rubricas table */}
            <Reveal>
                <div
                    data-testid="prestacao-table"
                    className="border-2 border-[var(--ink)] overflow-hidden"
                >
                    <div className="hidden md:grid grid-cols-12 gap-4 bg-[var(--ink)] text-white px-6 py-4 font-display text-[11px] tracking-[0.24em] uppercase">
                        <div className="col-span-6">Rubrica</div>
                        <div className="col-span-2 text-right">Aprovado</div>
                        <div className="col-span-2 text-right">Executado</div>
                        <div className="col-span-2 text-right">%</div>
                    </div>
                    {PRESTACAO.rubricas.map((r, i) => (
                        <div
                            key={r.rubrica}
                            data-testid={`rubrica-${i}`}
                            className="grid grid-cols-12 gap-4 px-6 py-5 border-b border-[var(--ink)]/15 last:border-0 items-center hover:bg-[var(--sun)]/10 transition-colors duration-300"
                        >
                            <div className="col-span-12 md:col-span-6 font-editorial italic text-lg md:text-xl">
                                {r.rubrica}
                            </div>
                            <div className="col-span-4 md:col-span-2 md:text-right">
                                <div className="text-[10px] tracking-[0.24em] uppercase md:hidden text-[var(--ink)]/50">
                                    Aprovado
                                </div>
                                <div className="font-body text-sm md:text-base">
                                    {r.aprovado}
                                </div>
                            </div>
                            <div className="col-span-4 md:col-span-2 md:text-right">
                                <div className="text-[10px] tracking-[0.24em] uppercase md:hidden text-[var(--ink)]/50">
                                    Executado
                                </div>
                                <div className="font-body font-semibold text-sm md:text-base">
                                    {r.executado}
                                </div>
                            </div>
                            <div className="col-span-4 md:col-span-2 md:text-right">
                                <div className="relative h-2 w-full bg-[var(--ink)]/10 overflow-hidden">
                                    <motion.div
                                        initial={{ width: "0%" }}
                                        whileInView={{
                                            width: `${r.percentual}%`,
                                        }}
                                        viewport={{
                                            once: true,
                                            margin: "-40px",
                                        }}
                                        transition={{
                                            duration: 1.2,
                                            ease: EASE,
                                            delay: 0.1 + i * 0.05,
                                        }}
                                        className="h-full bg-[var(--sun)]"
                                    />
                                </div>
                                <div className="mt-1 font-display text-[11px] tracking-[0.24em] text-[var(--ink)]/70">
                                    {r.percentual}%
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </Reveal>

            {/* Documentos */}
            <div className="mt-16 grid grid-cols-12 gap-6">
                <div className="col-span-12 md:col-span-5">
                    <div className="flex items-center gap-3 text-[11px] tracking-[0.32em] uppercase text-[var(--ink)]/60 font-body">
                        <span className="inline-block h-px w-10 bg-[var(--ink)]/40" />
                        <span>Documentação</span>
                    </div>
                    <Reveal>
                        <h3 className="mt-6 font-editorial italic text-4xl md:text-5xl text-[var(--ink-2)] leading-tight max-w-sm">
                            Arquivos disponíveis para consulta.
                        </h3>
                    </Reveal>
                    <p className="mt-6 font-body text-[14px] text-[var(--ink)]/70 max-w-sm">
                        Para receber os documentos oficiais em PDF, entre em
                        contato com a coordenação.
                    </p>
                </div>
                <div className="col-span-12 md:col-span-6 md:col-start-7 flex flex-col gap-4">
                    {PRESTACAO.documentos.map((d, i) => (
                        <Reveal key={d.nome} delay={i * 0.05}>
                            <a
                                href={`mailto:${SITE.email}?subject=${encodeURIComponent(
                                    "Solicitação: " + d.nome,
                                )}`}
                                data-testid={`doc-${i}`}
                                className="group flex items-center gap-6 border border-[var(--ink)]/20 p-6 hover:border-[var(--ink)] hover:bg-[var(--ink)] hover:text-white transition-all duration-300"
                            >
                                <FileText
                                    size={22}
                                    className="text-[var(--ink)]/60 group-hover:text-[var(--sun)] flex-shrink-0 transition-colors"
                                />
                                <div className="flex-1">
                                    <div className="font-editorial italic text-xl md:text-2xl leading-tight">
                                        {d.nome}
                                    </div>
                                    <div className="mt-1 font-body text-[13px] opacity-70">
                                        {d.desc}
                                    </div>
                                </div>
                                <Download
                                    size={18}
                                    className="text-[var(--ink)]/40 group-hover:text-[var(--sun)] group-hover:-translate-y-0.5 transition-all"
                                />
                            </a>
                        </Reveal>
                    ))}
                </div>
            </div>
        </div>
    </section>
);

// ============================================================
// CTA
// ============================================================
const ProjetoCTA = () => (
    <section
        data-testid="projeto-cta"
        className="relative bg-[var(--sun)] text-[var(--ink)] py-24 md:py-28 overflow-hidden"
    >
        <div className="container-x">
            <div className="grid grid-cols-12 gap-6 items-center">
                <div className="col-span-12 md:col-span-8">
                    <Reveal>
                        <h2 className="font-display text-[13vw] md:text-[6.5vw] lg:text-[5vw] leading-[0.9]">
                            Quer apoiar? <br />
                            <span className="font-editorial italic font-medium">
                                converse com a gente.
                            </span>
                        </h2>
                    </Reveal>
                    <p className="mt-6 font-body text-[15px] max-w-lg">
                        Empresas, instituições e pessoas físicas que queiram
                        contribuir com o projeto ou receber mais informações.
                    </p>
                </div>
                <div className="col-span-12 md:col-span-4 flex md:justify-end">
                    <Link
                        to="/"
                        data-testid="projeto-cta-membro"
                        onClick={() => {
                            setTimeout(() => {
                                document
                                    .getElementById("membro")
                                    ?.scrollIntoView({ behavior: "smooth" });
                            }, 100);
                        }}
                        className="btn-brutal bg-[var(--ink)] text-[var(--sun)] border-[var(--ink)]"
                        style={{ boxShadow: "6px 6px 0 rgba(0,0,0,0.35)" }}
                    >
                        Seja Membro
                        <ArrowUpRight size={16} strokeWidth={2.5} />
                    </Link>
                </div>
            </div>

            {/* Partners repeated */}
            <div className="mt-16 pt-10 border-t-2 border-[var(--ink)] flex flex-wrap items-center gap-x-10 gap-y-4">
                <span className="font-display text-[11px] tracking-[0.32em] uppercase">
                    Parceiros ·
                </span>
                {PARTNERS.map((p) => (
                    <span
                        key={p}
                        className="font-editorial italic text-lg md:text-xl"
                    >
                        {p}
                    </span>
                ))}
            </div>
        </div>
    </section>
);

// ============================================================
// PAGE
// ============================================================
export default function ProjetoMeninasVencedoras() {
    useEffect(() => {
        window.scrollTo(0, 0);
        document.title =
            "Meninas Vencedoras · Projeto AENHCC · Eletrobras · 2025—2026";
    }, []);

    return (
        <div className="App" data-testid="projeto-root">
            <div className="noise" aria-hidden="true" />
            <Header />
            <main>
                <ProjetoHero />
                <ProjetoObjetivos />
                <ProjetoImpacto />
                <ProjetoTimeline />
                <ProjetoCronograma />
                <ProjetoPrestacao />
                <ProjetoCTA />
            </main>
            <Footer />
        </div>
    );
}
